import React, {
  useCallback, useMemo, useState, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import { Header } from '@boomi/magnetosphere';
import AppConstants from '@common/AppConstants';
import Util from '@services/util';
import Features from '@utils/features';
// @ts-ignore
import { useBoomiContext } from '@context/BoomiContext';
import { useBoomiAgentFoundationContext } from '@context/BoomiAgentFoundationContext';
import { hasPrivilege } from '@utils/data/rbacService';
import UserPrivilege from '@utils/data/userPrivileges';
import SubNavigation from '../SubNavigation/SubNavigation';
import createNavigationUtils from '@utils/agent-foundation/navigationUtils';
import { handleLeftMenuLinkClick } from '../LeftMenuManager/LeftMenuManager';
import SaveConfirmationDialog from '@components/AgentBuilderWizard/SaveConfirmationDialog';
import SaveConfirmationHelper from '@utils/agent-foundation/SaveConfirmationHelper';
import AgentBuilderDialog from '@components/AgentBuilder/AgentBuilderDialog/AgentBuilderDialog';
import useHeaderHeight from './useHeaderHeight';

function MastHead({
  subNavMenu,
  isMobileView,
  newMastheadEnabled,
  userResponse,
  searchIconEventHandler,
}: any) {
  const [t] = useTranslation('common');
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);

  const {
    setBuilderDialogTitle,
    setShowBuilderDialog,
    setShowPreview,
    setTestMessages,
    setIsReffererTool,
    abortRequests,
    userPrivileges,
    showBuilderDialog,
  } = useBoomiAgentFoundationContext();
  const {
    setConversation,
    setConversationId,
    setShowConversationPage,
  } = useBoomiContext();
  const [isSaveConfirmationDialogOpen, setIsSaveConfirmationDialogOpen] = useState(false);
  const [discardConfirmationCallback,
    setDiscardConfirmationCallback,
  ] = useState<() => void>(() => {});

  const navigationUtils = createNavigationUtils({
    setShowBuilderDialog,
    setTestMessages,
    setIsSaveConfirmationDialogOpen: () => {},
    clearBoomiAIConversation: () => {},
    clearCustomAgentConversation: () => {},
    abortRequests,
    setConversationId,
    setConversation,
    setShowPreview,
    navigate,
    setIsReffererTool,
    setBuilderDialogTitle,
    t,
  });

  const accountId = Util.getCookie('ACCOUNT_ID');
  const goBackLink = Util.isDevelopmentMode() ? 'ai' : `/BoomiAI.html#ai;accountId=${
    accountId as string
  }`;

  // hook to set header height variables
  useHeaderHeight(headerRef, isMobileView, newMastheadEnabled);

  const checkCurrentUrl = () => {
    const currentUrl = window.location.href;
    const expectedPath = `/BoomiAI.html#ai;accountId=${accountId}`;
    return currentUrl.includes(expectedPath);
  };
  function filterSubmenusByPrivilege(submenus: any[]) {
    return submenus.filter((submenu) => {
      if (submenu.link === '/agents' || submenu.link === '/home') {
        return hasPrivilege(userPrivileges, UserPrivilege.AGENT_VIEW);
      }
      if (submenu.link === '/agent-designer') {
        return hasPrivilege(userPrivileges, UserPrivilege.AGENT_CREATE)
               && hasPrivilege(userPrivileges, UserPrivilege.AGENT_EDIT);
      }
      if (submenu.link === '/tools') {
        return hasPrivilege(userPrivileges, UserPrivilege.AGENT_CREATE)
               && hasPrivilege(userPrivileges, UserPrivilege.AGENT_EDIT);
      }
      if (submenu.link === '/sources') {
        return hasPrivilege(userPrivileges, UserPrivilege.AGENT_CREATE)
               && hasPrivilege(userPrivileges, UserPrivilege.AGENT_EDIT)
               && Features.isSourcesManagementEnabled();
      }
      if (submenu.link === '/chat') {
        return hasPrivilege(userPrivileges, UserPrivilege.AGENT_INTERACT);
      }
      if (submenu.link === '/environments') {
        return hasPrivilege(userPrivileges, UserPrivilege.AGENT_CREATE)
               && hasPrivilege(userPrivileges, UserPrivilege.AGENT_EDIT);
      }
      if (submenu.link === '/packages-deployments') {
        return hasPrivilege(userPrivileges, UserPrivilege.AGENT_VIEW);
      }
      return true;
    });
  }
  const goBackLinkHandler = useCallback(() => {
    const tempLink = document.createElement('a');
    tempLink.href = goBackLink;
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
  }, [goBackLink]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const handleSignOut = useCallback(() => {
    window.location.reload();
  }, []);
  const onBurgerIconEventHandler = (isClicked: boolean) => {
    if (isClicked) {
      setSidebarOpen((prev) => !prev);
    }
  };
  const showAIHomePage = useCallback(() => false, []);

  const pageInfo = {
    name: 'Agentstudio',
    homePage: {
      url: '/',
      title: 'Agentstudio',
    },
    redirect: {
      url: '',
      title: '',
    },
  };
  const memoizedPageInfo = useMemo(() => pageInfo, []);
  let showLeftNav = false;

  if (isMobileView) {
    showLeftNav = !newMastheadEnabled;
  } else {
    showLeftNav = true;
  }
  const filteredLeftNavSubmenus = filterSubmenusByPrivilege(AppConstants.LEFT_NAV_SUBMENUS);
  const subNavMenuWithLeftNav = structuredClone(subNavMenu);
  subNavMenuWithLeftNav[0].submenus = filteredLeftNavSubmenus;
  const newMastHeadSubNavMenu = newMastheadEnabled && isMobileView
    ? subNavMenuWithLeftNav
    : subNavMenu;

  const actionMap: Record<string, () => void> = {
    '/BoomiAI.html#/agent-designer': navigationUtils.buildAgent,
    '/BoomiAI.html#/tools': navigationUtils.manageTools,
    '/BoomiAI.html#/sources': navigationUtils.manageSources,
    '/BoomiAI.html#/environments': navigationUtils.goToEnvironments,
    '/BoomiAI.html#/packages-deployments': navigationUtils.goToPackagesDeployments,
    '/BoomiAI.html#/agents': navigationUtils.goToAgentList,
    '/BoomiAI.html#/chat': navigationUtils.goToConsumerExperience,
    '/BoomiAI.html#/home': navigationUtils.navigateToAgentGarden,
  };
  const onNavigateHandler = useCallback(() => {
    if (!checkCurrentUrl()) {
      goBackLinkHandler();
    } else {
      setShowConversationPage(false);
    }
  }, []);
  const navigationHandler = (menu: any) => {
    const performAction = () => {
      if (menu.link?.startsWith('http://') || menu.link?.startsWith('https://')) {
        window.location.href = menu.link;
      } else if (actionMap[menu.link]) {
        actionMap[menu.link]();
      } else {
        navigate(menu.link);
      }
    };
    if (typeof menu.link === 'string' && isMobileView && newMastheadEnabled) {
      handleLeftMenuLinkClick(
        performAction,
        setIsSaveConfirmationDialogOpen,
        setDiscardConfirmationCallback,
      );
    } else {
      onNavigateHandler();
    }
  };

  const closeAgentBuilder = () => {
    setIsReffererTool(false);
    setShowBuilderDialog(false);
    setShowPreview(false);
    setBuilderDialogTitle(t('agent_builder_dialog_title_agent_designer'));
  };
  return (
    <>
      {isSaveConfirmationDialogOpen && (
        <SaveConfirmationDialog
          handleExit={async () => {
            setIsSaveConfirmationDialogOpen(false);
            document.body.style.overflow = 'unset';
            await SaveConfirmationHelper.callMethod('saveData');
            discardConfirmationCallback();
          }}
          handleCancel={() => {
            setIsSaveConfirmationDialogOpen(false);
            document.body.style.overflow = 'unset';
            discardConfirmationCallback();
          }}
          handleClose={() => {
            setIsSaveConfirmationDialogOpen(false);
            document.body.style.overflow = 'unset';
          }}
          isSaveDisabled={SaveConfirmationHelper.getMethod('isSaveDisabled') ?? (() => false)}
        />
      )}
      <div
        ref={headerRef}
        className={[
          'app_masthead',
          showLeftNav === false && 'has-mobile-nav',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <Header
          checkForDevelopment={
            process.env.NODE_ENV === 'development'
            && Boolean(process.env.REACT_APP_DEV_MODE)
          }
          onSignOutSuccess={handleSignOut}
          showBoomiAi={showAIHomePage}
        // handleResponsiveness
          burgerIconEventHandler={onBurgerIconEventHandler}
          onBurgerIconClick={sidebarOpen}
          pageInfo={memoizedPageInfo}
          {...(Features.isAgentGardenFlagEnabled(userResponse?.user)
            ? { subNav: newMastHeadSubNavMenu }
            : {})}
          onNavigation={navigationHandler}
          searchIconEventHandler={searchIconEventHandler}
        />
        {!newMastheadEnabled
          && Features.isAgentGardenFlagEnabled(userResponse?.user) && (
            <SubNavigation />
        )}
      </div>

      {showBuilderDialog && (
        <AgentBuilderDialog onCancel={closeAgentBuilder} />
      )}
    </>
  );
}

export default MastHead;
